/**
 * 微信云开发相关请求辅助函数
 * 从 node-sdk 中提取，整合到 adapter-node 统一管理
 */
import * as fs from 'fs'
import { getCloudbaseContext } from './context'
import type { ICustomReqOpts } from './types'

// ========== 微信业务函数（从 node-sdk wx/index.ts 整合） ==========

import { E } from './utils'
import { ERROR } from './constants'

// ========== 微信 Token 辅助函数 ==========

const CLOUDBASE_ACCESS_TOKEN_PATH = '/.tencentcloudbase/wx/cloudbase_access_token'
const maxCacheAge = 10 * 60 * 1000
const cloudbaseAccessTokenInfo = { token: '', timestamp: 0 }

function loadWxCloudbaseAccesstoken(): string {
  if (cloudbaseAccessTokenInfo.token && Date.now() - cloudbaseAccessTokenInfo.timestamp < maxCacheAge) {
    return cloudbaseAccessTokenInfo.token
  }

  try {
    if (process.env.TENCENTCLOUD_RUNENV === 'WX_LOCAL_SCF' || process.env.TENCENTCLOUD_RUNENV === 'SCF') {
      if (fs.existsSync(CLOUDBASE_ACCESS_TOKEN_PATH)) {
        cloudbaseAccessTokenInfo.token = fs.readFileSync(CLOUDBASE_ACCESS_TOKEN_PATH).toString()
        cloudbaseAccessTokenInfo.timestamp = Date.now()
        return cloudbaseAccessTokenInfo.token
      }
    }
  } catch (e) {
    console.warn('[TCB][ERROR]: loadWxCloudbaseAccesstoken error: ', e)
  }

  return ''
}

export function getWxCloudToken(): { wxCloudApiToken: string; wxCloudbaseAccesstoken?: string } {
  const context: any = getCloudbaseContext()
  const { TRIGGER_SRC, WX_TRIGGER_API_TOKEN_V0, WX_API_TOKEN, WX_CLOUDBASE_ACCESSTOKEN = '' } = context

  const wxCloudToken: any = {}

  if (TRIGGER_SRC === 'timer') {
    wxCloudToken.wxCloudApiToken = WX_TRIGGER_API_TOKEN_V0 || WX_API_TOKEN || ''
  } else {
    wxCloudToken.wxCloudApiToken = WX_API_TOKEN || ''
  }

  if (!wxCloudToken.wxCloudApiToken) {
    wxCloudToken.wxCloudbaseAccesstoken = WX_CLOUDBASE_ACCESSTOKEN || loadWxCloudbaseAccesstoken()
  }

  return wxCloudToken
}

// ========== buildAdminUrl 辅助函数 ==========

/** 判断 envId 是否可用作域名前缀（比通用格式校验更严格） */
function isValidEnvDomain(envId: string): boolean {
  return /^[a-z][a-z0-9-]*[a-z0-9]$/.test(envId)
}

let seqNum = 0
function genSeqId(): string {
  seqNum += 1
  const eventId = `${Date.now().toString(16)}_${(seqNum).toString(16)}`
  const context: any = getCloudbaseContext()
  const { TCB_SEQID = '' } = context
  return TCB_SEQID ? `${TCB_SEQID}-${eventId}` : eventId
}

/**
 * 构建 /admin 路径的完整 URL
 * @param config - cloudbase 配置
 * @param scfContext - 云函数 context（含 requestId）
 * @param serviceUrl - 自定义服务端 URL（覆盖默认值）
 */
export function buildAdminUrl(config: any, scfContext?: any, serviceUrl?: string): string {
  const envId: string = typeof config.env === 'symbol'
    ? (process.env.TCB_ENV || process.env.SCF_NAMESPACE || '')
    : (config.env || '')
  const region = config.region || process.env.TENCENTCLOUD_REGION || ''
  const seqId = genSeqId()

  const endpoint = region
    ? `${region}.tcb-api.tencentcloudapi.com`
    : 'tcb-api.tencentcloudapi.com'
  const envEndpoint = isValidEnvDomain(envId) ? `${envId}.${endpoint}` : endpoint

  const protocol = 'https'
  const path = '/admin'
  const defaultUrl = `${protocol}://${envEndpoint}${path}`

  const url = serviceUrl || defaultUrl

  const qs = scfContext
    ? `env=${envId}&seqId=${seqId}&scfRequestId=${scfContext.requestId}`
    : `env=${envId}&seqId=${seqId}`

  return url.includes('?') ? `${url}${qs}` : `${url}?${qs}`
}

// ========== 微信请求函数 ==========

/**
 * 为微信请求 params 补充 token 和 sessionToken 公共字段
 */
function appendWxTokenParams(params: any): void {
  const context: any = getCloudbaseContext()
  const { wxCloudApiToken, wxCloudbaseAccesstoken } = getWxCloudToken()
  params.wxCloudApiToken = wxCloudApiToken
  if (wxCloudbaseAccesstoken) {
    params.wxCloudbaseAccesstoken = wxCloudbaseAccesstoken
  }
  params.tcb_sessionToken = context.TCB_SESSIONTOKEN || ''
}

export interface ISendWxRequestOptions {
  /** app.request 实例（NodeRequest），需具有 post 和 send 方法 */
  requestInstance: any
  /** cloudbase 配置 */
  config: any
  /** 云函数入口 scfContext（含 requestId） */
  scfContext?: any
  /** 自定义服务端 URL */
  serviceUrl?: string
}

/**
 * 使用 JSON 格式发送 wx 请求（用于 callWxOpenApi）
 * @param options - 请求实例和配置
 * @param action - 请求动作（如 'wx.api'）
 * @param params - 请求参数
 * @param reqOpts - 自定义请求选项（如 timeout）
 */
export async function sendWxRequest(
  options: ISendWxRequestOptions,
  action: string,
  params: any,
  reqOpts?: ICustomReqOpts
) {
  appendWxTokenParams(params)

  return options.requestInstance.send(
    action,
    params,
    { pathname: 'web', endPointMode: 'GATEWAY' },
    reqOpts ? { timeout: reqOpts.timeout } : undefined
  )
}

/**
 * 使用 multipart/form-data 格式发送 wx 请求（用于 callCompatibleWxOpenApi / callWxPayApi / wxCallContainerApi）
 * 自动处理 form-data 构建、签名和发送
 * 与旧版 SDK 的请求格式（multipart/form-data + /admin 路径）完全一致
 *
 * @param options - 请求实例和配置
 * @param action - 请求动作（如 'wx.openApi'、'wx.wxPayApi'、'wx.wxCallContainerApi'）
 * @param params - 请求参数
 * @param reqOpts - 自定义请求选项（如 timeout）
 */
export async function sendWxFormDataRequest(
  options: ISendWxRequestOptions,
  action: string,
  params: any,
  reqOpts?: ICustomReqOpts
): Promise<any> {
  appendWxTokenParams(params)

  // 延迟加载 form-data 包
  let FormData: any
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    FormData = require('form-data')
  } catch (e) {
    throw new Error('缺少依赖 form-data，请执行 npm install form-data')
  }

  // 使用 form-data 包构建 FormData（兼容 Node.js < 18 的云函数环境）
  const formData = new FormData()
  formData.append('action', action)
  for (const key of Object.keys(params)) {
    if (params[key] !== undefined) {
      formData.append(key, params[key])
    }
  }

  // 构建签名用的纯对象（getReqOptions 用它做 multipart 签名）
  const plainObj: any = { action, ...params }
  ;(formData as any).__plainObj = plainObj

  // 构建 admin URL
  const url = buildAdminUrl(options.config, options.scfContext, options.serviceUrl)

  // 通过 request.post 发送（responseType 为 buffer 以兼容旧版二进制响应格式）
  const res = await options.requestInstance.post(
    {
      url,
      body: formData,
      headers: formData.getHeaders(),
      responseType: 'buffer',
    },
    reqOpts ? { timeout: reqOpts.timeout } : undefined
  )

  // 返回 response data
  if (res.data) {
    return res.data
  }
  return res
}

/**
 * 校验跨帐号调用配置
 */
function validateCrossAccount(config: any, opts: any = {}): void {
  const getCrossAccountInfo = opts.getCrossAccountInfo || config.getCrossAccountInfo
  if (getCrossAccountInfo) {
    throw E({
      ...ERROR.INVALID_PARAM,
      message: 'invalid config: getCrossAccountInfo',
    })
  }
}

/**
 * 获取微信请求所需的 wxOptions 参数
 * @param config - cloudbase 配置
 * @param scfContext - 云函数 context
 * @param serviceUrl - 服务端注入 URL
 * @param requestInstance - request 实例
 */
export function getWxOptions(config: any, scfContext: any, serviceUrl: string, requestInstance: any): ISendWxRequestOptions {
  return { requestInstance, config, scfContext, serviceUrl }
}

/**
 * callWxOpenApi - 调用微信开放接口（JSON 格式）
 */
export async function callWxOpenApi(
  wxOptions: ISendWxRequestOptions,
  { apiName, apiOptions, cgiName, requestData }: { apiName: string; apiOptions?: any; cgiName?: string; requestData: any },
  opts?: ICustomReqOpts
) {
  let transformRequestData: any
  try {
    transformRequestData = requestData ? JSON.stringify(requestData) : ''
  } catch (e: any) {
    throw E({ ...e, code: ERROR.INVALID_PARAM.code, message: '对象出现了循环引用' })
  }

  validateCrossAccount(wxOptions.config, opts)

  const params: any = {
    apiName,
    apiOptions,
    cgiName,
    requestData: transformRequestData,
  }

  const res = await sendWxRequest(wxOptions, 'wx.api', params, opts)

  if (res.code) {
    return res
  }

  let result: any
  try {
    result = JSON.parse(res.responseData)
  } catch (e) {
    result = res.responseData
  }
  return {
    result,
    requestId: res.requestId,
  }
}

/**
 * 通用的 form-data 格式微信 API 调用（内部复用）
 * callCompatibleWxOpenApi / callWxPayApi / wxCallContainerApi 均委托此函数
 */
async function callWxFormDataApi(
  action: string,
  wxOptions: ISendWxRequestOptions,
  { apiName, apiOptions, cgiName, requestData }: { apiName: string; apiOptions?: any; cgiName?: string; requestData: any },
  opts?: ICustomReqOpts
) {
  validateCrossAccount(wxOptions.config, opts)
  return sendWxFormDataRequest(wxOptions, action, { apiName, apiOptions, cgiName, requestData }, opts)
}

/** callCompatibleWxOpenApi - 调用兼容的微信开放接口（form-data 格式） */
export const callCompatibleWxOpenApi = (
  wxOptions: ISendWxRequestOptions,
  apiOpts: { apiName: string; apiOptions?: any; cgiName?: string; requestData: any },
  opts?: ICustomReqOpts
) => callWxFormDataApi('wx.openApi', wxOptions, apiOpts, opts)

/** callWxPayApi - 调用微信支付接口（form-data 格式） */
export const callWxPayApi = (
  wxOptions: ISendWxRequestOptions,
  apiOpts: { apiName: string; apiOptions?: any; cgiName?: string; requestData: any },
  opts?: ICustomReqOpts
) => callWxFormDataApi('wx.wxPayApi', wxOptions, apiOpts, opts)

/** wxCallContainerApi - 调用微信云托管容器接口（form-data 格式） */
export const wxCallContainerApi = (
  wxOptions: ISendWxRequestOptions,
  apiOpts: { apiName: string; apiOptions?: any; cgiName?: string; requestData: any },
  opts?: ICustomReqOpts
) => callWxFormDataApi('wx.wxCallContainerApi', wxOptions, apiOpts, opts)
